import * as THREE from 'three';

interface Tracer {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  dir: THREE.Vector3;
  speed: number;
  remaining: number; // distance the bolt head still has to travel
  active: boolean;
  fresh: boolean;    // spawned this frame — render once at the muzzle first
}

/**
 * Gunfire bolts: short glowing projectiles that TRAVEL from muzzle to target
 * instead of hanging as a static beam. (A static beam near the muzzle gets
 * overtaken by the moving car within a frame or two, which reads as shots
 * coming from behind you.)
 */
export class TracerPool {
  private pool: Tracer[] = [];

  constructor(private parent: THREE.Object3D, size = 24) {
    const geo = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < size; i++) {
      const material = new THREE.MeshBasicMaterial({
        color: 0xffdd66,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, material);
      mesh.visible = false;
      parent.add(mesh);
      this.pool.push({
        mesh,
        material,
        dir: new THREE.Vector3(),
        speed: 0,
        remaining: 0,
        active: false,
        fresh: false,
      });
    }
  }

  fire(
    from: THREE.Vector3,
    to: THREE.Vector3,
    color = 0xffdd66,
    width = 0.06,
    speed = 480
  ): void {
    const tracer = this.pool.find((t) => !t.active);
    if (!tracer) return;

    const dist = from.distanceTo(to);
    if (dist < 0.5) return;
    // short bolt whose TAIL starts exactly at the muzzle
    const boltLen = Math.min(2.2, dist * 0.4);

    tracer.dir.copy(to).sub(from).normalize();
    tracer.speed = speed;
    tracer.remaining = dist;
    tracer.active = true;
    tracer.fresh = true;

    tracer.mesh.visible = true;
    // mesh center starts half a bolt-length ahead of the muzzle
    tracer.mesh.position.copy(from).addScaledVector(tracer.dir, boltLen / 2);
    tracer.mesh.scale.set(width, width, boltLen);
    tracer.mesh.lookAt(to);
    tracer.material.color.setHex(color);
    tracer.material.opacity = 0.95;
  }

  update(dt: number): void {
    for (const tracer of this.pool) {
      if (!tracer.active) continue;

      // update() runs before this frame's render — without this guard a
      // 700 m/s bolt is already ~8m downrange the first time you SEE it
      if (tracer.fresh) {
        tracer.fresh = false;
        continue;
      }

      const step = tracer.speed * dt;
      tracer.mesh.position.addScaledVector(tracer.dir, step);
      tracer.remaining -= step;

      // fade out over the last stretch of travel
      if (tracer.remaining < 15) {
        tracer.material.opacity = Math.max(0, (tracer.remaining / 15) * 0.95);
      }

      if (tracer.remaining <= 0) {
        tracer.active = false;
        tracer.mesh.visible = false;
      }
    }
  }
}
